// PRE RMA — Synchronisation CPN, même principe que offline/syncQueue.ts
// pour le RCE (conflits = SERVEUR GAGNE, une modification suit le même
// principe hors-ligne qu'une création).
import { api } from '../api/client.ts';
import {
  listerEnAttente, marquerSynchronise, marquerRefuse, marquerConflit, sauvegarderLocalement,
  mettreEnCacheCpn, sauvegarderLotLocalement, obtenirEnAttente, remplacerPayloadEnFile,
  supprimerLocal, supprimerDuCacheCpn, listerParPeriode, purgerElementsSynchronisesCpn,
} from './dbCpn.ts';
import type { LocalCpn } from './dbCpn.ts';
import type { CpnIndicateur } from '../../../shared/business-logic/cpn.ts';
import { nettoyerCpn } from '../../../shared/business-logic/nettoyage.ts';
import { normaliserIndicateurs } from '../../../shared/business-logic/cpn.ts';
import type { CpnImportee } from '../../../shared/business-logic/importCpn.ts';

/** Saisie CPN sous sa forme stockée : N° registre / âge en nombres (ou null), les 22 indicateurs toujours présents (true/false). */
function payloadPropre(payload: CpnPayload): CpnPayload {
  const nombre = (v: unknown) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
  return {
    periodeId: payload.periodeId,
    numeroRegistre: nombre(payload.numeroRegistre),
    ageAns: nombre(payload.ageAns),
    indicateurs: normaliserIndicateurs(payload.indicateurs as Partial<Record<string, unknown>>),
    // Champs ajoutés : ajoutés seulement s'ils existent (les anciennes saisies et les imports en masse gardent leur forme d'origine).
    ...(typeof payload.datePrestation === 'string' && payload.datePrestation ? { datePrestation: payload.datePrestation } : {}),
    ...(typeof payload.htaDepistage === 'boolean' ? { htaDepistage: payload.htaDepistage } : {}),
    ...(payload.numeroRceLie !== undefined ? { numeroRceLie: payload.numeroRceLie ? String(payload.numeroRceLie).trim() : null } : {}),
  };
}

export interface CpnPayload {
  periodeId: string;
  numeroRegistre: number | null;
  ageAns: number | null;
  indicateurs: Partial<Record<CpnIndicateur, boolean>>;
  /** Date de prestation AAAA-MM-JJ (obligatoire pour toute nouvelle saisie). */
  datePrestation?: string | null;
  /** Dépistage HTA réalisé ? (non renseigné = absent) */
  htaDepistage?: boolean | null;
  /** N° RCE de la même personne si elle a aussi été vue en consultation le même jour. */
  numeroRceLie?: string | null;
}

export type EtatSynchroCpn = 'IDLE' | 'SYNCING' | 'ERROR';
type Ecouteur = (etat: EtatSynchroCpn, nbEnAttente: number) => void;

const ecouteurs = new Set<Ecouteur>();
let enCours = false;

export function ecouterEtatSynchroCpn(fn: Ecouteur): () => void {
  ecouteurs.add(fn);
  return () => ecouteurs.delete(fn);
}

async function notifier(etat: EtatSynchroCpn) {
  const nb = (await listerEnAttente()).filter((i) => i.syncStatus === 'PENDING').length;
  ecouteurs.forEach((fn) => fn(etat, nb));
}

export async function enregistrerCpn(periodeId: string, csbId: string, payload: CpnPayload): Promise<{ localId: string }> {
  const localId = crypto.randomUUID();
  const item: LocalCpn = {
    localId, operation: 'CREATE', periodeId, csbId,
    payload: { ...payloadPropre(payload), periodeId, localId },
    syncStatus: 'PENDING', createdAt: Date.now(), updatedAt: Date.now(),
  };
  await sauvegarderLocalement(item);
  await notifier('IDLE');
  if (navigator.onLine) traiterFileCpn();
  return { localId };
}

export async function enregistrerModificationCpn(
  periodeId: string, csbId: string, cpnId: string, baseVersion: number, payload: CpnPayload
): Promise<{ localId: string }> {
  const localId = crypto.randomUUID();
  const item: LocalCpn = {
    localId, operation: 'UPDATE', periodeId, csbId, cpnId, baseVersion,
    payload: { ...payloadPropre(payload), periodeId },
    syncStatus: 'PENDING', createdAt: Date.now(), updatedAt: Date.now(),
  };
  await sauvegarderLocalement(item);
  await notifier('IDLE');
  if (navigator.onLine) traiterFileCpn();
  return { localId };
}

/** IMPORT en masse (texte collé / fichier) : tout est écrit d'un coup dans IndexedDB (une transaction : tout ou rien), envoyé ensuite comme une saisie normale. */
export async function importerCpn(periodeId: string, csbId: string, lot: CpnImportee[]): Promise<number> {
  const maintenant = Date.now();
  const items: LocalCpn[] = lot.map((l, i) => {
    const localId = crypto.randomUUID();
    return {
      localId, operation: 'CREATE', periodeId, csbId,
      payload: { ...payloadPropre({ periodeId, numeroRegistre: l.numeroRegistre, ageAns: l.ageAns, indicateurs: l.indicateurs }), periodeId, localId },
      syncStatus: 'PENDING', createdAt: maintenant + i, updatedAt: maintenant + i,
    };
  });
  await sauvegarderLotLocalement(items);
  await notifier('IDLE');
  if (navigator.onLine) traiterFileCpn();
  return items.length;
}

/**
 * Corrige une saisie CPN faite sur cet appareil et pas encore envoyée (la création en attente est remplacée par la version corrigée).
 * Si l'envoi a eu lieu entre-temps, bascule sur une vraie modification du serveur.
 */
export async function modifierCpnLocale(localId: string, periodeId: string, csbId: string, payload: CpnPayload): Promise<void> {
  const item = await obtenirEnAttente(localId);
  if (!item) throw new Error('Cette saisie a été supprimée ou envoyée : actualisez la liste.');
  if (item.syncStatus === 'SYNCED' && item.serverId) {
    await enregistrerModificationCpn(periodeId, csbId, item.serverId, item.serverVersion ?? 1, payload);
    return;
  }
  await remplacerPayloadEnFile(localId, payloadPropre(payload) as unknown as Record<string, unknown>);
  await notifier('IDLE');
  if (navigator.onLine) traiterFileCpn();
}

/**
 * SUPPRIME une saisie CPN (confirmation déjà obtenue auprès de l'utilisateur par l'écran). Fonctionne HORS CONNEXION.
 *  - saisie jamais envoyée au serveur (faite hors connexion) : simple retrait de la file, rien à annuler côté serveur ;
 *  - saisie déjà enregistrée : ordre DELETE placé dans la file + retrait immédiat du cache local ; envoyé dès que le réseau revient.
 *    Les modifications encore en attente sur cette saisie sont abandonnées.
 * Côté serveur : suppression LOGIQUE (DELETE /cpn/:id), journalisée.
 */
export async function supprimerCpn(
  cpn: { id?: string; localId?: string; version?: number; numeroRegistre?: number },
  periodeId: string,
  csbId: string
): Promise<'RETIRE_DE_LA_FILE' | 'SUPPRESSION_PLANIFIEE'> {
  if (!cpn.id) {
    if (!cpn.localId) throw new Error('Saisie introuvable.');
    const item = await obtenirEnAttente(cpn.localId);
    if (item?.syncStatus === 'SYNCED' && item.serverId) {
      return supprimerCpn({ ...cpn, id: item.serverId, version: item.serverVersion }, periodeId, csbId);
    }
    // Jamais envoyée : on la retire. Si l'envoi est en cours à cet instant, traiterFileCpn détecte le retrait et planifie la suppression.
    await supprimerLocal(cpn.localId);
    await notifier('IDLE');
    return 'RETIRE_DE_LA_FILE';
  }
  if (cpn.version === undefined) throw new Error('Version de la saisie inconnue : actualisez la liste puis réessayez.');
  // Les modifications en attente sur cette saisie n'ont plus de sens.
  for (const it of await listerParPeriode(periodeId)) {
    if (it.cpnId === cpn.id && it.operation === 'UPDATE' && it.syncStatus !== 'SYNCED') await supprimerLocal(it.localId);
  }
  const localId = crypto.randomUUID();
  const item: LocalCpn = {
    localId, operation: 'DELETE', periodeId, csbId, cpnId: cpn.id, baseVersion: cpn.version,
    payload: { numeroRegistre: cpn.numeroRegistre ?? null },
    syncStatus: 'PENDING', createdAt: Date.now(), updatedAt: Date.now(),
  };
  await sauvegarderLocalement(item);
  await supprimerDuCacheCpn(cpn.id);
  await notifier('IDLE');
  if (navigator.onLine) traiterFileCpn();
  return 'SUPPRESSION_PLANIFIEE';
}

/** Panne à ne pas traiter comme un refus : réseau/délai (pas de status), 5xx, 408, 429, 401 (session — déjà renouvelée par api/client.ts). */
function estTransitoire(err: any): boolean {
  const st = err?.status;
  // 500 = plantage applicatif sur CETTE saisie → refus définitif. Les autres 5xx (502/503/504, 52x, démarrage à froid) restent des pannes.
  return !st || st === 401 || st === 408 || st === 429 || (st >= 500 && st !== 500);
}

/** Refus définitif : validation / refus métier (4xx) ou crash 500 → « Refusé », la file continue. */
function estRefusDefinitif(err: any): boolean {
  const st = err?.status;
  return !!st && !estTransitoire(err) && (st === 500 || (st >= 400 && st < 500));
}

function motifRefus(err: any): string {
  if (err?.details?.length) return err.details.join(' / ');
  if (err?.status === 500) return "Erreur interne du serveur : cette saisie est mise de côté (rien n'est perdu). Corrigez-la ou utilisez « Forcer la synchronisation ».";
  return err?.message ?? 'Saisie refusée par le serveur.';
}

/**
 * Traite la file CPN — STRICTEMENT SÉQUENTIEL : une requête à la fois, jamais de lot ni de Promise.all (bas débit 2G/3G).
 * - Refus métier du serveur (400/403/404/422/423…) : l'élément est marqué « Refusé / À vérifier » (syncStatus ERROR + message),
 *   mis de côté, et on envoie IMMÉDIATEMENT le suivant (la file n'est jamais bloquée par une ligne invalide).
 * - Panne (réseau coupé, délai de 90 s dépassé, 5xx, 429, session non renouvelable) : la passe S'ARRÊTE, les éléments restent PENDING
 *   et repartent au retour du réseau / au minuteur de 60 s (inutile d'attendre un délai par élément restant).
 */
export async function traiterFileCpn(options: { reessayerRefuses?: boolean } = {}): Promise<void> {
  if (enCours || !navigator.onLine) return;
  enCours = true;
  let interrompue = false;
  await notifier('SYNCING');
  try {
    // « Forcer la synchronisation » : les saisies refusées sont retentées UNE fois pendant cette passe.
    if (options.reessayerRefuses) {
      for (const i of await listerEnAttente()) {
        if (i.syncStatus === 'ERROR') await sauvegarderLocalement({ ...i, syncStatus: 'PENDING', erreur: undefined, updatedAt: Date.now() });
      }
    }
    const enAttente = (await listerEnAttente()).filter((i) => i.syncStatus === 'PENDING').sort((a, b) => a.createdAt - b.createdAt);
    for (const item of enAttente) {
      if (interrompue) break;
      try {
        if (item.operation === 'CREATE') {
          // Nettoyage juste avant l'appel API : trim des chaînes, N° de registre / âge → Number(valeur) || 0.
          const res = await api.post<{ id: string; version: number }>('/cpn', nettoyerCpn(item.payload));
          if (!(await obtenirEnAttente(item.localId))) {
            // La saisie a été supprimée par l'utilisateur PENDANT l'envoi : elle existe maintenant côté serveur → on planifie sa suppression.
            await sauvegarderLocalement({
              localId: crypto.randomUUID(), operation: 'DELETE', periodeId: item.periodeId, csbId: item.csbId, cpnId: res.id, baseVersion: res.version,
              payload: { numeroRegistre: item.payload.numeroRegistre ?? null },
              syncStatus: 'PENDING', createdAt: Date.now(), updatedAt: Date.now(),
            });
            continue;
          }
          await marquerSynchronise(item.localId, res.id, res.version);
        } else if (item.operation === 'DELETE') {
          // Suppression logique côté serveur ; 404 = déjà supprimée (rejeu, autre appareil) = succès.
          try { await api.delete(`/cpn/${item.cpnId}?baseVersion=${item.baseVersion}`); }
          catch (e: any) { if (e.status !== 404) throw e; }
          await supprimerLocal(item.localId); // confirmé par le serveur : on peut purger l'ordre
          if (item.cpnId) {
            await supprimerDuCacheCpn(item.cpnId);
            await purgerElementsSynchronisesCpn(item.cpnId);
          }
        } else {
          const res = await api.patch<{ id: string; version: number }>(
            `/cpn/${item.cpnId}`,
            { ...nettoyerCpn(item.payload), baseVersion: item.baseVersion }
          );
          await marquerSynchronise(item.localId, res.id, res.version);
        }
      } catch (err: any) {
        if (err.status === 409 && item.operation === 'DELETE') {
          await marquerConflit(
            item.localId,
            err.versionServeur,
            'Suppression non appliquée : cette saisie a été modifiée entre-temps par un autre appareil ou utilisateur. ' +
              'Actualisez la liste, vérifiez la saisie, puis supprimez-la de nouveau si nécessaire.'
          );
        } else if (err.status === 409 && item.operation === 'UPDATE') {
          await marquerConflit(
            item.localId,
            err.versionServeur,
            'Cette saisie a été modifiée entre-temps par un autre appareil ou utilisateur. ' +
              'La version du serveur a été conservée ; rouvrez la saisie pour repartir de la version à jour si votre correction est toujours nécessaire.'
          );
        } else if (estTransitoire(err)) {
          interrompue = true; // l'élément reste PENDING
        } else if (estRefusDefinitif(err)) {
          // 400/422/403/404/423… ou crash 500 : mention « Refusé » écrite dans IndexedDB, puis on passe IMMÉDIATEMENT à la saisie suivante.
          await marquerRefuse(item.localId, motifRefus(err), err.status).catch(() => { /* IndexedDB indisponible : reste PENDING */ });
        } else {
          interrompue = true; // statut inattendu (3xx…) : on ne perd rien, on réessaiera
        }
      }
    }
  } finally {
    enCours = false;
    await notifier(navigator.onLine && !interrompue ? 'IDLE' : 'ERROR');
  }
}

export async function rafraichirCacheCpn(periodeId: string): Promise<Array<Record<string, unknown>>> {
  const rows = await api.get<Array<Record<string, unknown> & { id: string }>>(`/cpn?periodeId=${periodeId}`);
  await mettreEnCacheCpn(periodeId, rows);
  return rows;
}

window.addEventListener('online', () => { traiterFileCpn(); });
setInterval(() => { if (navigator.onLine) traiterFileCpn(); }, 60_000);
